import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { z } from 'zod';

// Message types shared with the RACEMAP App (mobile-apps/tracking-app/src/lib/webviewConsent.ts).
const REQUEST_TYPE = 'racemap-location-permission-request';
const RESULT_TYPE = 'racemap-location-permission-result';
// The answer waits for the app's disclosure and the OS dialog
const ANSWER_TIMEOUT = OneMinuteInMillis;

const ResultSchema = z.object({ type: z.literal(RESULT_TYPE), granted: z.boolean() });

export function isInApp(): boolean {
  return typeof window.ReactNativeWebView?.postMessage === 'function';
}

// The app's handler uses PermissionsAndroid, which always answers "denied" on iOS.
export function isAndroidApp(): boolean {
  return isInApp() && /Android/i.test(window.navigator.userAgent);
}

function parseResult(event: MessageEvent): { granted: boolean } | null {
  // The app injects its messages with an empty or 'null' origin
  if (event.origin !== '' && event.origin !== 'null') return null;
  try {
    const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    const result = ResultSchema.safeParse(data);
    return result.success ? { granted: result.data.granted } : null;
  } catch {
    return null;
  }
}

/** Asks the app for the phone's location permission; resolves null when the app does not answer. */
export function requestAppLocationPermission(): Promise<boolean | null> {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => {
      window.removeEventListener('message', handleMessage);
      resolve(null);
    }, ANSWER_TIMEOUT);
    function handleMessage(event: MessageEvent) {
      const result = parseResult(event);
      if (result == null) return;
      window.clearTimeout(timeout);
      window.removeEventListener('message', handleMessage);
      resolve(result.granted);
    }
    window.addEventListener('message', handleMessage);
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: REQUEST_TYPE }));
  });
}
